import { Tabs } from "@next-platform/client-common-spicy-ui";
import type { KeyboardEvent } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { Col, Row } from "@/components/customComponents";
import type { DatePreset, IDateRange } from "@/pages/CreatorAnalytics/types";
import { toLocalDateStr, validateDateRangeDraft } from "@/pages/CreatorAnalytics/utils";

import CustomDateRangeEditor from "./CustomDateRangeEditor";

interface IDateRangeFilterProps {
  dateRange: IDateRange;
  preset: DatePreset;
  onPresetChange: (preset: DatePreset) => void;
  onDateRangeChange: (range: IDateRange) => void;
  "data-testid"?: string;
}

const ROOT_TEST_ID = "DateRangeFilter";
const ENTER_KEY = "Enter";

const PRESET_TABS: { name: DatePreset; label: string }[] = [
  { name: "7d", label: "7 Days" },
  { name: "30d", label: "30 Days" },
  { name: "90d", label: "90 Days" },
  { name: "custom", label: "Custom" },
];

const DateRangeFilter = ({ dateRange, preset, onPresetChange, onDateRangeChange, "data-testid": testId = ROOT_TEST_ID }: IDateRangeFilterProps) => {
  const fromStr = useMemo(() => toLocalDateStr(dateRange.from), [dateRange.from]);
  const toStr = useMemo(() => toLocalDateStr(dateRange.to), [dateRange.to]);
  // Recompute on every render so a tab left open across midnight rolls over
  // to the new day instead of treating the new "today" as a future date.
  // The cost is one Date allocation per render, which is negligible compared
  // to the cost of a stale validation rejecting a legitimate range.
  const todayStr = toLocalDateStr(new Date());

  // The native date input fires onChange on every keystroke / spinner click,
  // so committing immediately would refetch analytics for partial years like
  // 0002-MM-DD that the backend rejects as Invalid Payload. Buffer the value
  // in a local draft and only commit on Apply.
  const [stateDraftFromStr, setStateDraftFromStr] = useState(fromStr);
  const [stateDraftToStr, setStateDraftToStr] = useState(toStr);

  useEffect(() => {
    setStateDraftFromStr(fromStr);
  }, [fromStr]);

  useEffect(() => {
    setStateDraftToStr(toStr);
  }, [toStr]);

  // Constructed at midnight so a draft equal to today compares as equal,
  // not "in the future". Memoised on the day string (not on []) so the value
  // updates if the tab survives across midnight, but stays stable within the
  // same day to keep the validation memo below from invalidating per render.
  const todayAtMidnight = useMemo(() => {
    const [year, month, day] = todayStr.split("-").map(Number);
    return new Date(year, month - 1, day);
  }, [todayStr]);

  const { isFromInvalid, isToInvalid, canApply, nextFrom, nextTo } = useMemo(
    () =>
      validateDateRangeDraft({
        draftFromStr: stateDraftFromStr,
        draftToStr: stateDraftToStr,
        savedFromStr: fromStr,
        savedToStr: toStr,
        savedFrom: dateRange.from,
        savedTo: dateRange.to,
        todayAtMidnight,
      }),
    [stateDraftFromStr, stateDraftToStr, fromStr, toStr, dateRange.from, dateRange.to, todayAtMidnight],
  );

  const handlePresetChange = useCallback((tabName: string) => onPresetChange(tabName as DatePreset), [onPresetChange]);

  const handleApply = useCallback(() => {
    if (!canApply) {
      return;
    }
    onDateRangeChange({ from: nextFrom, to: nextTo });
  }, [canApply, nextFrom, nextTo, onDateRangeChange]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === ENTER_KEY && canApply) {
        event.preventDefault();
        handleApply();
      }
    },
    [canApply, handleApply],
  );

  return (
    <Col className="gap-3" data-testid={testId}>
      <Row className="flex-wrap items-center gap-2">
        <Tabs tabItems={PRESET_TABS} activeTab={preset} onTabChange={handlePresetChange} />
      </Row>
      {preset === "custom" ? (
        <CustomDateRangeEditor
          draftFromStr={stateDraftFromStr}
          draftToStr={stateDraftToStr}
          todayStr={todayStr}
          isFromInvalid={isFromInvalid}
          isToInvalid={isToInvalid}
          canApply={canApply}
          onDraftFromChange={setStateDraftFromStr}
          onDraftToChange={setStateDraftToStr}
          onApply={handleApply}
          onKeyDown={handleKeyDown}
        />
      ) : null}
    </Col>
  );
};

export default DateRangeFilter;
